<template>
  <div class="body">
    <DescriptorHeader @returnToMainPage="handleReturnToMainPage" />

    <OverlayLoader v-if="isLoading" />

    <template v-else-if="!isFormSubmitted">
      <div class="header-wrapper">
        <h1 class="page-title">
          {{ capitalizedDescriptor }} Billing & Payment Support
        </h1>
        <h3 class="page-subtitle">
          Need help with your payment? We are always here to assist you.
        </h3>
      </div>
      <div class="container container-form-payment">
        <SupportForm @formSubmitted="handleFormSubmit" />
        <PaymentInfo />
      </div>
      <div class="container-form-faq">
        <FaqBlock />
      </div>
    </template>

    <template v-else>
      <div class="container container-submitted">
        <FormSubmissionResult :email="submittedEmail" @back="handleReturnToMainPage" />
        <FaqBlock />
      </div>
    </template>

    <LegalAddressFooter
      v-if="legalAddress"
      :legalAddress="legalAddress"
    />
  </div>
</template>

<script setup lang="ts">
import { useHead } from '@unhead/vue'

import { useDescriptorPage } from '@/composables/useDescriptorPage'

import DescriptorHeader from './DescriptorHeader.vue'
import FaqBlock from './FaqBlock.vue'
import FormSubmissionResult from './FormSubmissionResult.vue'
import LegalAddressFooter from './LegalAddressFooter.vue'
import OverlayLoader from './OverlayLoader.vue'
import PaymentInfo from './PaymentInfo/PaymentInfo.vue'
import SupportForm from './SupportForm.vue'
import favicon from '../assets/favicon.ico?no-inline'

useHead({
  link: [{ key: 'favicon', rel: 'icon', type: 'image/x-icon', href: favicon }],
})

const {
  capitalizedDescriptor,
  legalAddress,
  isFormSubmitted,
  isLoading,
  submittedEmail,
  handleFormSubmit,
  handleReturnToMainPage,
} = useDescriptorPage()
</script>

<style scoped lang="scss">
.body {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
}

.container {
  display: flex;
  flex: 1;
  justify-content: center;
  width: 100%;
  max-width: 1200px;
  margin: 0 auto 80px;
  gap: 20px;
  padding-right: 40px;
  padding-left: 40px;
}

.container > * {
  flex: 1 1 50%;
  min-width: 0;
}

.container-form-payment {
  align-items: stretch;
}

.container-form-payment > * {
  align-self: stretch;
}

.container-submitted {
  align-items: flex-start;
  margin-top: 198px;
}

.container-submitted > * {
  align-self: flex-start;
}

.container-form-faq {
  display: flex;
  align-items: flex-start;
  flex: 1;
  justify-content: center;
  width: 100%;
  max-width: 1200px;
  margin: 0 auto 56px;
  gap: 20px;
  padding-right: 40px;
  padding-left: 40px;
}

.header-wrapper {
  padding: 0 20px;
}

h1.page-title {
  align-self: stretch;
  margin-top: 144px;
  margin-bottom: 12px;
  font-family: 'Open Sans', sans-serif;
  font-size: 36px;
  font-weight: var(--font-weight-bold);
}

.page-subtitle {
  margin: 0 auto 48px;
  text-align: center;
  color: #666;
  font-size: 16px;
  font-weight: var(--font-weight-regular);
}

.submitted-data {
  width: 100%;
  max-width: 600px;
  margin-top: 20px;
  padding: 16px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  background: white;

  &__title {
    margin: 0 0 12px;
    color: #333;
  }

  &__item {
    margin: 8px 0;
    color: #666;
  }
}

.payments-container {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

@media (max-width: 920px) {
  .container {
    flex-direction: column;
    margin: 0 auto 40px;
    padding: 0 16px;
  }

  .container-form-faq {
    flex-direction: column;
    padding: 0 16px;
  }
}

@media (max-width: 480px) {
  h1.page-title {
    margin-top: 96px;
    margin-bottom: 8px;
    text-align: left;
    font-size: 24px;
  }

  .page-subtitle {
    margin-bottom: 24px;
    text-align: left;
  }

  .container-submitted {
    margin-top: 96px;
  }
}
</style>
